import apiClient, { buildAuthHeaders, resolveApiUrl } from 'services/apiClient';
import { recordRumEvent } from 'services/awsRumService';

export const PRODUCT_ANALYTICS_EVENTS = {
    PORTAL_OPENED: 'portal_opened',
    PORTAL_SECTION_VIEWED: 'portal_section_viewed',
    ANALYSIS_LOADED: 'analysis_loaded',
    ANALYSIS_VIEW_CHANGED: 'analysis_view_changed',
    ANALYSIS_DATE_RANGE_CHANGED: 'analysis_date_range_changed',
    ANALYSIS_VEHICLE_MODEL_FILTER_APPLIED: 'analysis_vehicle_model_filter_applied',
    ANALYSIS_REFRESH_REQUESTED: 'analysis_refresh_requested',
    FLEET_OVERVIEW_OPENED: 'fleet_overview_opened',
    FLEET_VEHICLE_SELECTED: 'fleet_vehicle_selected',
    EXPORT_XLSX_CLICKED: 'export_xlsx_clicked',
};

const PRODUCT_ANALYTICS_ENDPOINT = 'shiftware/api/v1/product-analytics/events/';
const SESSION_STORAGE_KEY = 'shiftwareProductAnalyticsSessionId';
const FLUSH_DELAY_MS = 1000;
const MAX_BATCH_SIZE = 25;
const MAX_QUEUE_SIZE = 100;
const SAFE_PROPERTY_KEYS = new Set([
    'section',
    'previous_section',
    'analysis_type',
    'analysis_types',
    'analysis_type_count',
    'from_view',
    'to_view',
    'date_from',
    'date_to',
    'preset_key',
    'selected_model_count',
    'total_model_count',
    'selected_vehicle_count',
    'total_vehicle_count',
    'all_models_selected',
    'loaded_analysis_types',
    'row_counts',
    'result_status',
    'status',
    'row_count',
    'filtered_row_count',
    'visible_column_count',
    'export_type',
    'source',
    'trigger',
]);

let sessionId = '';
let currentAuthToken = '';
let pendingEvents = [];
let flushTimer = null;
let lifecycleInitialized = false;

const isBrowser = () => typeof window !== 'undefined';

const isProductAnalyticsEnabled = () => (
    String(process.env.REACT_APP_PRODUCT_ANALYTICS_ENABLED || 'true').trim().toLowerCase() !== 'false'
);

const generateUuid = () => {
    if (isBrowser() && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.random() * 16 | 0;
        const value = char === 'x' ? random : ((random & 0x3) | 0x8);
        return value.toString(16);
    });
};

export const getProductAnalyticsSessionId = () => {
    if (sessionId) return sessionId;
    if (!isBrowser()) {
        sessionId = generateUuid();
        return sessionId;
    }

    try {
        const storedSessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (storedSessionId) {
            sessionId = storedSessionId;
            return sessionId;
        }

        sessionId = generateUuid();
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        return sessionId;
    } catch {
        sessionId = generateUuid();
        return sessionId;
    }
};

const sanitizeValue = (value) => {
    if (value === null || value === undefined || typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') return value.trim().slice(0, 200);
    if (Array.isArray(value)) {
        return value
            .slice(0, 20)
            .map(sanitizeValue)
            .filter((item) => item !== null && typeof item !== 'object');
    }
    if (typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value)
                .slice(0, 25)
                .map(([key, item]) => [String(key).slice(0, 80), sanitizeValue(item)])
                .filter(([, item]) => item !== null && typeof item !== 'object')
        );
    }
    return null;
};

export const sanitizeProductAnalyticsProperties = (properties = {}) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};

    return Object.fromEntries(
        Object.entries(properties)
            .filter(([key]) => SAFE_PROPERTY_KEYS.has(key))
            .map(([key, value]) => [key, sanitizeValue(value)])
            .filter(([, value]) => value !== null)
    );
};

export const sanitizeProductAnalyticsPage = (page = '') => (
    String(page || '').split('?', 1)[0].split('#', 1)[0].slice(0, 255)
);

export const configureProductAnalytics = ({ token } = {}) => {
    if (token) {
        currentAuthToken = token;
    }
};

const requeueEvents = (events) => {
    pendingEvents = [...events, ...pendingEvents].slice(0, MAX_QUEUE_SIZE);
};

const scheduleFlush = () => {
    if (flushTimer || !isBrowser()) return;

    flushTimer = window.setTimeout(() => {
        flushTimer = null;
        flushProductAnalyticsEvents();
    }, FLUSH_DELAY_MS);
};

export const flushProductAnalyticsEvents = async ({ token, keepalive = false } = {}) => {
    const authToken = token || currentAuthToken;
    if (!isProductAnalyticsEnabled() || !authToken || pendingEvents.length === 0) {
        return { accepted: 0, skipped: true };
    }

    const batch = pendingEvents.slice(0, MAX_BATCH_SIZE);
    pendingEvents = pendingEvents.slice(batch.length);
    const payload = { events: batch };

    try {
        if (keepalive && isBrowser() && typeof window.fetch === 'function') {
            const response = await window.fetch(resolveApiUrl(PRODUCT_ANALYTICS_ENDPOINT), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...buildAuthHeaders(authToken),
                },
                body: JSON.stringify(payload),
                keepalive: true,
            });
            if (!response.ok) {
                throw new Error(`Analytics request failed with ${response.status}`);
            }
        } else {
            await apiClient.post(PRODUCT_ANALYTICS_ENDPOINT, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    ...buildAuthHeaders(authToken),
                },
            });
        }
    } catch {
        requeueEvents(batch);
        return { accepted: 0, failed: true };
    }

    if (pendingEvents.length > 0) {
        scheduleFlush();
    }
    return { accepted: batch.length };
};

export const trackProductAnalyticsEvent = (eventName, {
    token,
    page,
    properties = {},
    mirrorToRum = true,
} = {}) => {
    if (!isProductAnalyticsEnabled() || !eventName) return null;

    if (token) {
        currentAuthToken = token;
    }

    const safeProperties = sanitizeProductAnalyticsProperties(properties);
    const event = {
        event_id: generateUuid(),
        event_name: eventName,
        occurred_at: new Date().toISOString(),
        session_id: getProductAnalyticsSessionId(),
        page: sanitizeProductAnalyticsPage(page || (isBrowser() ? window.location.pathname : '')),
        properties: safeProperties,
    };

    pendingEvents = [...pendingEvents, event].slice(-MAX_QUEUE_SIZE);
    if (mirrorToRum) {
        recordRumEvent(eventName, safeProperties);
    }
    scheduleFlush();
    return event;
};

export const initializeProductAnalyticsLifecycle = () => {
    if (!isBrowser() || lifecycleInitialized) return;
    lifecycleInitialized = true;

    const flushWithKeepalive = () => {
        flushProductAnalyticsEvents({ keepalive: true });
    };

    window.addEventListener('pagehide', flushWithKeepalive);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushWithKeepalive();
        }
    });
};

export const __resetProductAnalyticsForTests = () => {
    sessionId = '';
    currentAuthToken = '';
    pendingEvents = [];
    lifecycleInitialized = false;
    if (flushTimer && isBrowser()) {
        window.clearTimeout(flushTimer);
    }
    flushTimer = null;
};
